import { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Calendar as CalendarIcon, Clock, Video, Globe, CheckCircle2,
  ChevronLeft, ChevronRight, User, Mail, Phone, Building, Sparkles,
  ArrowLeft, Download, ShieldCheck
} from "lucide-react";
import { toast } from "sonner";

const TIME_SLOTS = [
  "09:00 AM", "10:00 AM", "11:30 AM", "01:30 PM", "02:45 PM", "04:00 PM"
];

const DATES = [
  { day: "Mon", date: "Nov 2", full: "Monday, November 2, 2026" },
  { day: "Tue", date: "Nov 3", full: "Tuesday, November 3, 2026" },
  { day: "Wed", date: "Nov 4", full: "Wednesday, November 4, 2026" },
  { day: "Thu", date: "Nov 5", full: "Thursday, November 5, 2026" },
  { day: "Fri", date: "Nov 6", full: "Friday, November 6, 2026" },
];

export default function PublicBooking() {
  const { slug } = useParams();
  const hostTitle = slug
    ? slug.replace(/-/g, " ").replace(/\b\w/g, l => l.toUpperCase())
    : "Master";

  const [selectedDate, setSelectedDate] = useState(DATES[0]);
  const [selectedTime, setSelectedTime] = useState(TIME_SLOTS[1]);
  const [step, setStep] = useState(1); // 1 = Select Time, 2 = Enter Details, 3 = Confirmed

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    notes: "",
  });
  const [bookingBusy, setBookingBusy] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email) {
      toast.error("Please enter your name and email address");
      return;
    }
    setBookingBusy(true);

    setTimeout(() => {
      // Save appointment to global calendar localStorage
      const newAppointment = {
        id: `apt-${Date.now()}`,
        client: formData.name,
        title: `Commercial Discovery Session with ${formData.company || formData.name}`,
        date: selectedDate.full,
        time: selectedTime,
        channel: "Google Meet",
        status: "Confirmed",
        email: formData.email,
        phone: formData.phone,
        notes: formData.notes
      };

      try {
        const saved = localStorage.getItem("ld_calendar_appointments");
        const list = saved ? JSON.parse(saved) : [];
        localStorage.setItem("ld_calendar_appointments", JSON.stringify([newAppointment, ...list]));
        window.dispatchEvent(new Event("calendar_appointment_added"));
      } catch {}

      setBookingBusy(false);
      setStep(3);
      toast.success("Appointment successfully confirmed!");
    }, 600);
  };

  const handleDownloadICS = () => {
    const icsData = `BEGIN:VCALENDAR
VERSION:2.0
PRODID:-//LaunchDeck OS//Calendar Event//EN
BEGIN:VEVENT
SUMMARY:Commercial Discovery Session with Colibri Media (${hostTitle})
DESCRIPTION:${formData.notes || "Client discovery call scheduled via LaunchDeck OS"}
DTSTART:20261102T170000Z
DTEND:20261102T173000Z
LOCATION:Google Meet (https://meet.google.com/colibri-media)
STATUS:CONFIRMED
END:VEVENT
END:VCALENDAR`;

    const blob = new Blob([icsData], { type: "text/calendar;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", `appointment_${selectedDate.date.replace(/\s+/g, "_")}.ics`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("Calendar invite (.ics) downloaded!");
  };

  return (
    <div
      className="min-h-screen py-10 px-4 sm:px-6 flex flex-col justify-between font-sans select-none"
      style={{
        background: "linear-gradient(180deg, #090E1A 0%, #0F172A 50%, #1E293B 100%)",
      }}
      data-testid="public-booking-page"
    >
      {/* Top Header */}
      <header className="max-w-4xl mx-auto w-full flex items-center justify-between pb-6">
        <Link to="/" className="flex items-center gap-2.5 group">
          <div className="w-9 h-9 rounded-xl bg-blue-600 text-white flex items-center justify-center font-bold text-sm shadow-md">
            <Sparkles className="w-5 h-5" />
          </div>
          <span className="font-extrabold text-base text-white tracking-tight">
            LaunchDeck VIP Scheduling
          </span>
        </Link>

        <div className="flex items-center gap-2 text-xs font-semibold text-emerald-400 bg-emerald-950/60 border border-emerald-800/60 px-3 py-1 rounded-full">
          <ShieldCheck className="w-3.5 h-3.5" />
          <span>Real-time Calendar Sync</span>
        </div>
      </header>

      {/* Main Booking Container */}
      <main className="max-w-4xl mx-auto w-full">
        <Card className="border border-slate-700/60 bg-slate-900/90 backdrop-blur-xl shadow-2xl rounded-2xl overflow-hidden grid grid-cols-1 md:grid-cols-3">
          {/* Left Column: Meeting Info */}
          <div className="p-6 sm:p-8 border-b md:border-b-0 md:border-r border-slate-800 space-y-6 bg-slate-950/60 text-white">
            <div className="space-y-2">
              <Badge className="bg-blue-600/20 text-blue-400 border-blue-500/30 text-xs font-semibold">
                Direct Client Booking
              </Badge>
              <h1 className="text-2xl font-black tracking-tight font-heading">
                Colibri Media Studio
              </h1>
              <p className="text-xs text-slate-400 leading-relaxed">
                Schedule a 30-minute discovery consultation to discuss your brand visual campaign, photo &amp; video production, or automated marketing retainers.
              </p>
            </div>

            <div className="space-y-3 pt-2 text-xs text-slate-300">
              <div className="flex items-center gap-2.5">
                <Clock className="w-4 h-4 text-blue-400 shrink-0" />
                <span>30 minutes duration</span>
              </div>
              <div className="flex items-center gap-2.5">
                <Video className="w-4 h-4 text-emerald-400 shrink-0" />
                <span>Google Meet video call (link sent via email)</span>
              </div>
              <div className="flex items-center gap-2.5">
                <Globe className="w-4 h-4 text-purple-400 shrink-0" />
                <span>Pacific Time (US &amp; Canada)</span>
              </div>
            </div>

            <div className="p-4 rounded-xl bg-slate-900/80 border border-slate-800 space-y-1">
              <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider block">Host:</span>
              <div className="font-bold text-sm text-white">{hostTitle} Host / Executive Director</div>
              <span className="text-xs text-emerald-400 font-semibold">Instant confirmation</span>
            </div>
          </div>

          {/* Right Column: Interactive Date, Time & Form */}
          <div className="md:col-span-2 p-6 sm:p-8 space-y-6">
            {step === 1 && (
              <div className="space-y-6">
                <div>
                  <h2 className="text-lg font-bold text-white tracking-tight">Select Date &amp; Time</h2>
                  <p className="text-xs text-slate-400 mt-0.5">Times are automatically displayed in your local timezone.</p>
                </div>

                {/* Date Selector Pills */}
                <div className="space-y-2">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Available Dates</span>
                  <div className="grid grid-cols-5 gap-2">
                    {DATES.map((d) => {
                      const isSelected = selectedDate.date === d.date;
                      return (
                        <button
                          key={d.date}
                          type="button"
                          onClick={() => setSelectedDate(d)}
                          className={`p-3 rounded-xl border text-center transition-all cursor-pointer ${
                            isSelected
                              ? "bg-blue-600 border-blue-500 text-white shadow-md shadow-blue-600/30 font-bold"
                              : "bg-slate-800/40 border-slate-800 text-slate-300 hover:bg-slate-800 hover:border-slate-700"
                          }`}
                        >
                          <div className="text-[11px] uppercase tracking-wider opacity-70">{d.day}</div>
                          <div className="text-sm font-bold mt-0.5">{d.date}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Time Slots */}
                <div className="space-y-2">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">
                    Available Slots for {selectedDate.full}
                  </span>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                    {TIME_SLOTS.map((t) => {
                      const isSelected = selectedTime === t;
                      return (
                        <button
                          key={t}
                          type="button"
                          onClick={() => setSelectedTime(t)}
                          className={`py-2.5 px-3 rounded-xl border text-xs font-semibold transition-all cursor-pointer ${
                            isSelected
                              ? "bg-blue-600 border-blue-500 text-white font-bold shadow-md"
                              : "bg-slate-800/40 border-slate-800 text-slate-300 hover:bg-slate-800 hover:border-slate-700"
                          }`}
                        >
                          {t}
                        </button>
                      );
                    })}
                  </div>
                </div>

                <Button
                  onClick={() => setStep(2)}
                  className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm shadow-md"
                >
                  Continue to Your Information →
                </Button>
              </div>
            )}

            {step === 2 && (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="flex items-center justify-between pb-2 border-b border-slate-800">
                  <div>
                    <h2 className="text-lg font-bold text-white tracking-tight">Your Details</h2>
                    <p className="text-xs text-slate-400">
                      {selectedDate.full} at {selectedTime}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() => setStep(1)}
                    className="text-xs text-slate-400 hover:text-white"
                  >
                    <ArrowLeft className="w-3.5 h-3.5 mr-1" /> Change Time
                  </Button>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-bold text-slate-300 block mb-1">Your Full Name *</label>
                    <Input
                      placeholder="e.g. Sarah Jenkins"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      className="bg-slate-950/60 border-slate-800 text-white text-sm h-10"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-300 block mb-1">Work Email *</label>
                    <Input
                      type="email"
                      placeholder="sarah@agency.com"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      required
                      className="bg-slate-950/60 border-slate-800 text-white text-sm h-10"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs font-bold text-slate-300 block mb-1">Mobile Phone (For SMS Reminder)</label>
                    <Input
                      placeholder="+1 (555) 000-0000"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="bg-slate-950/60 border-slate-800 text-white text-sm h-10 font-mono"
                    />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-slate-300 block mb-1">Company / Studio Name</label>
                    <Input
                      placeholder="e.g. Apex Visuals Co."
                      value={formData.company}
                      onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                      className="bg-slate-950/60 border-slate-800 text-white text-sm h-10"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-xs font-bold text-slate-300 block mb-1">Project Notes / Campaign Goals</label>
                  <Textarea
                    rows={3}
                    placeholder="Tell us what visual assets, commercial shoots, or marketing systems you'd like to scale..."
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    className="bg-slate-950/60 border-slate-800 text-white text-sm resize-none"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={bookingBusy}
                  className="w-full h-11 bg-blue-600 hover:bg-blue-700 text-white font-bold text-sm shadow-md"
                >
                  {bookingBusy ? "Confirming Session…" : "Confirm Discovery Session ✓"}
                </Button>
              </form>
            )}

            {step === 3 && (
              <div className="py-8 text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 flex items-center justify-center mx-auto shadow-lg">
                  <CheckCircle2 className="w-9 h-9" />
                </div>
                <h2 className="text-2xl font-black text-white tracking-tight">
                  You're Booked!
                </h2>
                <p className="text-sm text-slate-300 max-w-md mx-auto leading-relaxed">
                  A calendar invitation and Google Meet link have been dispatched to <strong className="text-white">{formData.email}</strong>.
                </p>

                <div className="p-4 rounded-xl bg-slate-950 border border-slate-800 max-w-sm mx-auto text-left text-xs text-slate-300 space-y-1.5 font-sans">
                  <div><strong>When:</strong> {selectedDate.full} at {selectedTime}</div>
                  <div><strong>Where:</strong> Google Meet (Video Call)</div>
                  <div><strong>Attendees:</strong> {formData.name} &amp; Colibri Media Studio</div>
                </div>

                <div className="pt-2 flex flex-col sm:flex-row items-center justify-center gap-3">
                  <Button
                    onClick={handleDownloadICS}
                    variant="outline"
                    className="text-xs font-semibold gap-1.5 border-slate-700 text-slate-200 hover:text-white"
                  >
                    <Download className="w-3.5 h-3.5" /> Add to Apple / Google Calendar (.ICS)
                  </Button>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-1.5 text-xs text-blue-400 hover:underline font-semibold"
                  >
                    Return to Portal
                  </Link>
                </div>
              </div>
            )}
          </div>
        </Card>
      </main>

      {/* Footer */}
      <footer className="max-w-4xl mx-auto w-full text-center text-xs text-slate-500 pt-6">
        Powered by LaunchDeck Calendar &amp; Scheduling Engine · Enterprise 256-Bit SSL Encrypted
      </footer>
    </div>
  );
}

